import { createRouter, createWebHistory } from "vue-router";
const HomeView = () => import("../views/HomeView.vue");
const LoginView = () => import("@/views/LoginView.vue");
const VerifyView = () => import("@/views/VerifyView.vue");
const Auth0CallbackView = () => import("@/views/Auth0CallbackView");
const DashboardView = () => import("@/views/DashboardView");
const AllDeviceReadingsView = () => import("@/views/AllDeviceReadingsView");
const ShareReadingsView = () => import("@/views/ShareReadingsView");
const WordleView = () => import("@/views/WordleView");

import auth0Client from "@/utils/auth0Client";
import constants from "@/utils/constants";
import logger from "@/utils/logger";
import {
  createSignInClickwrap,
  hasAcceptedSigninClickwrapApi,
} from "@/utils/pactsafe";
import * as AppStorage from "@/utils/appStorage";

// isGuestRoute meta property means that the route does not require auth (anyone can access it)
// If you do not explicitly pass isGuestRoute property, it is assumed that the route SHOULD BE AUTHED
const routes = [
  {
    path: "/",
    name: "Home",
    component: HomeView,
    redirect: { path: "dashboard" },
    children: [
      {
        path: "dashboard",
        name: "Dashboard",
        component: DashboardView,
        meta: { tabTitle: "Dashboard" },
      },
      {
        path: "readings/share",
        name: "ShareReadings",
        component: ShareReadingsView,
        meta: { tabTitle: "Share Readings" },
      },
      {
        path: "/wordle",
        name: "Wordle",
        component: WordleView,
        meta: { tabTitle: "Wordle" },
      },
      {
        path: "readings/:device",
        name: "AllDeviceReadings",
        component: AllDeviceReadingsView,
        meta: { tabTitle: "Device Readings" },
        beforeEnter: (to) => {
          /*
            Check to make sure the device param provided is one of the valid ones,
            if not, return to the main dashboard page
          */
          if (
            !Object.keys(constants.DEVICE_TYPE_TO_READINGS_API_PARAMS).includes(
              to.params.device
            )
          ) {
            return { name: "Dashboard" };
          }
        },
      },
    ],
  },
  {
    path: "/login",
    name: "Login",
    component: LoginView,
    meta: { isGuestRoute: true, tabTitle: "Login" },
  },
  {
    path: "/login/verify",
    name: "Verify",
    component: VerifyView,
    meta: { isGuestRoute: true, tabTitle: "Login" },
    beforeEnter: async (to) => {
      const signerValue = AppStorage.get("pactsafeSigner");
      await createSignInClickwrap("tosAndPpCheckbox", signerValue);
      const hasPreviouslyAcceptedClickwrap =
        await hasAcceptedSigninClickwrapApi(signerValue);
      to.params.hasPreviouslyAcceptedClickwrap = hasPreviouslyAcceptedClickwrap;
    },
  },
  {
    path: "/callback/auth0",
    name: "Auth0Callback",
    component: Auth0CallbackView,
    meta: { isGuestRoute: true },
  },
  // !!!IMPORTANT!!!: Keep this as the last route in the list. This acts as an automatic fallback when trying to access a non-existent route
  {
    path: "/:pathMatch(.*)*",
    redirect: { path: "dashboard" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  // Set the tab title dynamically when navigating to new route
  document.title = to.meta.tabTitle
    ? `${constants.DEFAULT_TAB_TITLE} - ${to.meta.tabTitle}`
    : constants.DEFAULT_TAB_TITLE;

  // Check if route requires auth and if so check if current auth session is valid
  if (!to.meta.isGuestRoute) {
    try {
      await auth0Client.checkSession();
    } catch (error) {
      logger.error(error);
      return { name: "Login" };
    }
  }
});

export default router;
